import { useEffect, useMemo, useRef, useState } from "react";
import { visualRect } from "../../lib/zoom.js";

// Кастомный плеер голосового в стиле Telegram: круглая play-кнопка,
// waveform из данных сообщения, кликабельная перемотка, транскрибация.
// Нативный <audio controls> выглядел чужеродно и не давал waveform.

const BAR_COUNT = 48;

// Обратная сторона buildTelegramWaveform (chat-view): base64 → 5-битные
// значения 0..31. Формат совпадает с voiceNote.waveform из TDLib.
const decodeTelegramWaveform = (base64: string): number[] => {
  let binary: string;
  try {
    binary = atob(base64);
  } catch {
    return [];
  }
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const count = Math.floor((bytes.length * 8) / 5);
  const values: number[] = [];
  for (let index = 0; index < count; index += 1) {
    const bitOffset = index * 5;
    const byteIndex = bitOffset >> 3;
    const shift = bitOffset & 7;
    let value = (bytes[byteIndex] ?? 0) >> shift;
    if (shift > 3) {
      value |= (bytes[byteIndex + 1] ?? 0) << (8 - shift);
    }
    values.push(value & 31);
  }
  return values;
};

// Когда у сообщения нет waveform-данных (старый бэкенд их не присылал),
// рисуем детерминированную псевдо-волну: сид от url/длительности, плавный
// «случайный» рельеф. Ровная гребёнка одинаковых столбиков выглядела как
// пунктир и выдавала заглушку; Telegram никогда не показывает плоскую волну.
const pseudoWaveform = (seedSource: string): number[] => {
  let seed = 2166136261;
  for (let index = 0; index < seedSource.length; index += 1) {
    seed = Math.imul(seed ^ seedSource.charCodeAt(index), 16777619);
  }
  const rand = (): number => {
    seed = Math.imul(seed ^ (seed >>> 13), 1274126177);
    return (seed >>> 0) / 4294967296;
  };
  const values: number[] = [];
  let level = 10 + rand() * 12;
  for (let index = 0; index < BAR_COUNT; index += 1) {
    level += (rand() - 0.5) * 12;
    level = Math.max(4, Math.min(29, level));
    values.push(Math.round(level));
  }
  return values;
};

// Приводим произвольное число значений к BAR_COUNT колонкам (максимум по окну,
// чтобы пики не терялись).
const resampleWaveform = (values: number[]): number[] => {
  if (values.length === 0) {
    return Array.from({ length: BAR_COUNT }, () => 9);
  }
  const result: number[] = [];
  for (let index = 0; index < BAR_COUNT; index += 1) {
    const start = Math.floor((index * values.length) / BAR_COUNT);
    const end = Math.max(start + 1, Math.floor(((index + 1) * values.length) / BAR_COUNT));
    let peak = 0;
    for (let offset = start; offset < end; offset += 1) {
      peak = Math.max(peak, values[offset] ?? 0);
    }
    result.push(peak);
  }
  return result;
};

const formatClock = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export interface VoiceMessageProps {
  url?: string | undefined;
  mimeType?: string | undefined;
  waveform?: string | undefined;
  durationSeconds?: number | undefined;
  transcript?: string | undefined;
  transcriptStatus?: "pending" | "done" | "failed" | undefined;
  onTranscribe?: (() => void) | undefined;
}

export const VoiceMessage = ({
  url,
  mimeType,
  waveform,
  durationSeconds,
  transcript,
  transcriptStatus,
  onTranscribe
}: VoiceMessageProps) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [mediaDuration, setMediaDuration] = useState<number | null>(null);
  const [transcriptOpen, setTranscriptOpen] = useState(true);

  const bars = useMemo(() => {
    const decoded = waveform ? decodeTelegramWaveform(waveform) : [];
    // Все нули = данных по сути нет (или бэкенд их не прислал) — псевдо-волна.
    if (decoded.some((value) => value > 0)) {
      return resampleWaveform(decoded);
    }
    return pseudoWaveform(url ?? `${durationSeconds ?? 0}`);
  }, [waveform, url, durationSeconds]);

  const effectiveDuration =
    mediaDuration && Number.isFinite(mediaDuration) && mediaDuration > 0
      ? mediaDuration
      : durationSeconds && durationSeconds > 0
        ? durationSeconds
        : 0;
  const progress = effectiveDuration > 0 ? Math.min(1, currentTime / effectiveDuration) : 0;

  useEffect(() => {
    // Смена url (файл докачался) — сбрасываем плеер.
    setIsPlaying(false);
    setCurrentTime(0);
    setMediaDuration(null);
  }, [url]);

  const togglePlayback = (): void => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (audio.paused) {
      void audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  };

  const seekTo = (ratio: number): void => {
    const audio = audioRef.current;
    if (!audio || effectiveDuration <= 0) {
      return;
    }
    const clamped = Math.max(0, Math.min(1, ratio));
    audio.currentTime = clamped * effectiveDuration;
    setCurrentTime(clamped * effectiveDuration);
  };

  return (
    <div className="voice-msg">
      {url ? (
        <audio
          ref={audioRef}
          // none, не metadata: строки виртуализованы и перемонтируются на
          // каждом проходе скролла — с metadata каждая видимая voice-строка
          // делала Range-запрос к gateway при каждом монтировании. Длительность
          // и так приходит в durationSeconds; точная mediaDuration подтянется
          // из onLoadedMetadata при первом Play.
          preload="none"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => {
            setIsPlaying(false);
            setCurrentTime(0);
          }}
          onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
          onLoadedMetadata={(event) => {
            const value = event.currentTarget.duration;
            if (Number.isFinite(value) && value > 0) {
              setMediaDuration(value);
            }
          }}
        >
          <source src={url} type={mimeType ?? "audio/ogg"} />
        </audio>
      ) : null}
      <div className="voice-msg__row">
        <button
          type="button"
          className="voice-msg__play"
          onClick={togglePlayback}
          disabled={!url}
          aria-label={isPlaying ? "Пауза" : "Слушать голосовое"}
          title={isPlaying ? "Пауза" : "Слушать"}
        >
          {isPlaying ? (
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <rect x="3.5" y="2.5" width="3.4" height="11" rx="1.1" />
              <rect x="9.1" y="2.5" width="3.4" height="11" rx="1.1" />
            </svg>
          ) : (
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M4.4 2.6c0-.9 1-1.5 1.8-1L13.4 6c.8.5.8 1.6 0 2.1l-7.2 4.4c-.8.5-1.8-.1-1.8-1V2.6z" />
            </svg>
          )}
        </button>
        <div className="voice-msg__body">
          <div
            className="voice-msg__wave"
            role="slider"
            aria-label="Позиция воспроизведения"
            aria-valuemin={0}
            aria-valuemax={Math.round(effectiveDuration)}
            aria-valuenow={Math.round(currentTime)}
            tabIndex={url ? 0 : -1}
            onClick={(event) => {
              // visualRect: clientX всегда в визуальных px, а rect в Safari
              // приходит в логических — без приведения перемотка при
              // UI-масштабе ≠ 1 бьёт мимо.
              const rect = visualRect(event.currentTarget);
              seekTo((event.clientX - rect.left) / rect.width);
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight") {
                event.preventDefault();
                seekTo(effectiveDuration > 0 ? (currentTime + 2) / effectiveDuration : 0);
              }
              if (event.key === "ArrowLeft") {
                event.preventDefault();
                seekTo(effectiveDuration > 0 ? (currentTime - 2) / effectiveDuration : 0);
              }
              if (event.key === " " || event.key === "Enter") {
                event.preventDefault();
                togglePlayback();
              }
            }}
          >
            {bars.map((value, index) => (
              <span
                key={index}
                className={
                  progress > 0 && index / BAR_COUNT <= progress
                    ? "voice-msg__bar voice-msg__bar--played"
                    : "voice-msg__bar"
                }
                style={{ height: `${Math.max(3, Math.round((value / 31) * 22))}px` }}
              />
            ))}
          </div>
          <div className="voice-msg__meta">
            <span className="voice-msg__time">
              {isPlaying || currentTime > 0
                ? `${formatClock(currentTime)} / ${formatClock(effectiveDuration)}`
                : effectiveDuration > 0
                  ? formatClock(effectiveDuration)
                  : "…"}
            </span>
            {!url ? <span className="voice-msg__loading">Загружаем…</span> : null}
          </div>
        </div>
        {onTranscribe && transcriptStatus !== "done" ? (
          <button
            type="button"
            className="voice-msg__transcribe"
            onClick={onTranscribe}
            disabled={transcriptStatus === "pending"}
            title="Распознать голосовое в текст (нужен Telegram Premium на аккаунте)"
            aria-label="Распознать голосовое в текст"
          >
            {transcriptStatus === "pending" ? "…" : "→A"}
          </button>
        ) : null}
        {transcriptStatus === "done" && transcript ? (
          <button
            type="button"
            className="voice-msg__transcribe voice-msg__transcribe--active"
            onClick={() => setTranscriptOpen((value) => !value)}
            title={transcriptOpen ? "Скрыть текст" : "Показать текст"}
            aria-label={transcriptOpen ? "Скрыть текст" : "Показать текст"}
          >
            →A
          </button>
        ) : null}
      </div>
      {transcriptStatus === "pending" ? (
        <div className="voice-msg__transcript voice-msg__transcript--pending">Распознаём…</div>
      ) : null}
      {transcriptStatus === "failed" ? (
        <div className="voice-msg__transcript voice-msg__transcript--failed">
          Не удалось распознать речь.
        </div>
      ) : null}
      {transcriptStatus === "done" && transcript && transcriptOpen ? (
        <div className="voice-msg__transcript">{transcript}</div>
      ) : null}
    </div>
  );
};
